import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Configuration } from '../../../components/BtoConfigurator/buildConfigurationString';
import { initOptional } from '../../common/miscUtils';

const initialState = {
  options: [] as Array<{ code: string | undefined; priceNet: number }>,
  grossPriceWithOptions: 0,
  netPriceWithoutOptions: 0,
  jatoId: null as null | string,
  configurationString: initOptional<string>(),
  previousConfigurationString: initOptional<string>(),
  isBtoConfigLocked: false as boolean,
};

export type BtoConfigState = typeof initialState;

const btoConfigSlice = createSlice({
  name: 'btoConfig',
  initialState,
  selectors: {
    selectBtoOptions: (state: BtoConfigState) => {
      return state.options;
    },
    selectBtoPrices: (state: BtoConfigState) => {
      return {
        grossPriceWithOptions: state.grossPriceWithOptions,
        netPriceWithoutOptions: state.netPriceWithoutOptions,
      };
    },
    selectJatoId: (state: BtoConfigState) => {
      return state.jatoId;
    },
    selectBtoConfigLocked: (state: BtoConfigState) => {
      return state.isBtoConfigLocked;
    },
    selectConfigurationString: (state: BtoConfigState) => {
      return state.configurationString;
    },
    selectPreviousConfigurationString: (state: BtoConfigState) => {
      return state.previousConfigurationString;
    },
  },
  reducers: {
    setJatoId(state, { payload }: PayloadAction<string | null>) {
      state.jatoId = payload;
    },
    setBtoConfigLocked(state, { payload }: PayloadAction<boolean>) {
      state.isBtoConfigLocked = payload;
    },
    setBtoConfigOptions(state, { payload }: PayloadAction<Array<{ code: string | undefined; priceNet: number }>>) {
      state.options = payload;
    },
    setBtoConfigPrices(
      state,
      { payload }: PayloadAction<{ grossPriceWithOptions: number; netPriceWithoutOptions: number }>
    ) {
      state.grossPriceWithOptions = payload.grossPriceWithOptions;
      state.netPriceWithoutOptions = payload.netPriceWithoutOptions;
    },
    resetBtoConfig() {
      return initialState;
    },
    setConfigurationString(state, { payload }: PayloadAction<{ config: Configuration; preservePrevious?: boolean }>) {
      const { config, preservePrevious } = payload;
      const configJson = JSON.stringify(config);

      if (!preservePrevious) {
        state.previousConfigurationString = state.configurationString ?? configJson;
      }

      state.configurationString = configJson;
    },
  },
});

export const btoConfigSelectors = btoConfigSlice.selectors;
export const {
  resetBtoConfig,
  setBtoConfigPrices,
  setBtoConfigOptions,
  setJatoId,
  setBtoConfigLocked,
  setConfigurationString,
} = btoConfigSlice.actions;
export const btoConfigReducer = btoConfigSlice.reducer;
